import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert } from 'react-native';
import AppTextInput from './AppTextInput';
import locationService from '../services/locationService';
import type { BusinessLocation } from '../types/location';

interface Props {
  visible: boolean;
  onClose: () => void;
  businessId: string;
}

const ManageLocationsModal: React.FC<Props> = ({ visible, onClose, businessId }) => {
  const [locations, setLocations] = useState<BusinessLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const list = await locationService.getLocations(businessId);
      setLocations(list);
    } catch (e) {
      console.warn('Locations load error:', e);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) load();
  }, [visible, businessId]);

  const add = async () => {
    if (!name.trim()) {
      Alert.alert('Error', 'Location name is required.');
      return;
    }
    try {
      const loc = await locationService.addLocation(businessId, {
        name: name.trim(),
        address: address.trim() ? { street: address.trim() } : undefined,
        isActive: true,
      });
      setLocations((prev) => [...prev, loc]);
      setName('');
      setAddress('');
    } catch (e) {
      Alert.alert('Error', 'Could not add location.');
    }
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Locations</Text>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} contentContainerStyle={{ paddingBottom: 24 }}>
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Add location</Text>
            <AppTextInput style={styles.input} placeholder="Location name *" value={name} onChangeText={setName} />
            <AppTextInput style={styles.input} placeholder="Address (optional)" value={address} onChangeText={setAddress} />
            <TouchableOpacity style={styles.primaryButton} onPress={add}>
              <Text style={styles.primaryButtonText}>Add</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Current locations</Text>
            {loading ? (
              <Text style={styles.muted}>Loading…</Text>
            ) : locations.length === 0 ? (
              <Text style={styles.muted}>No locations found.</Text>
            ) : (
              locations.map((l) => (
                <View key={l.id} style={styles.locationRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.locationName}>{l.name}</Text>
                    {!!l.address?.street && <Text style={styles.muted}>{l.address.street}</Text>}
                  </View>
                  {l.id === 'primary' && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>Primary</Text>
                    </View>
                  )}
                </View>
              ))
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: 20, fontWeight: 'bold', color: 'white' },
  closeButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: { color: 'white', fontSize: 16, fontWeight: 'bold' },
  content: { flex: 1, padding: 20 },
  card: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 10,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#333', marginBottom: 10 },
  input: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
    fontSize: 15,
  },
  primaryButton: { backgroundColor: '#28a745', paddingVertical: 12, borderRadius: 8 },
  primaryButtonText: { color: 'white', fontWeight: '800', textAlign: 'center' },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  locationName: { fontSize: 15, fontWeight: '800', color: '#333' },
  muted: { color: '#666', fontSize: 13, marginTop: 2 },
  badge: { backgroundColor: '#E9F2FF', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 6 },
  badgeText: { color: '#007bff', fontWeight: '800', fontSize: 12 },
});

export default ManageLocationsModal;

